import React, { useState } from 'react';
import {
  ChevronLeft, FileText, Users, Layers, ShieldCheck, UploadCloud, Paperclip, Tag,
} from 'lucide-react';
import {
  TASK_TYPES, PRIORITIES, SITES, STORES, USERS,
} from '../constants';
import { autoDetectRisk } from '../utils/helpers';
import { SectionTitle, FormInput, FormSelect, FormTextArea } from './Shared';

export default function CreateTask({ onCreate, navigateTo, currentUser }) {
  const [form, setForm] = useState({
    title: '', type: TASK_TYPES[0], asin: '', sku: '', store: STORES[0], site: SITES[0],
    assignee: currentUser, collaborators: [], priority: 'medium', dueDate: '',
    materials: '', files: [], description: '', needLeoApproval: false,
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');

  function handleChange(key, value) { setForm(prev => ({ ...prev, [key]: value })); }
  function handleCollaborators(val) {
    setForm(prev => ({ ...prev, collaborators: val.split(',').map(s => s.trim()).filter(Boolean) }));
  }
  function handleFileUpload(e) {
    const files = Array.from(e.target.files || []).map(f => ({ name: f.name, size: `${Math.round(f.size / 1024)}KB`, url: '#' }));
    setForm(prev => ({ ...prev, files: [...prev.files, ...files] }));
  }
  function addTag() {
    if (!tagInput.trim()) return;
    setForm(prev => ({ ...prev, tags: [...new Set([...prev.tags, tagInput.trim()])] }));
    setTagInput('');
  }
  function removeTag(tag) {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  }

  function handleSubmit() {
    if (!form.title.trim()) { alert('请输入任务标题'); return; }
    if (!form.dueDate) { alert('请选择预计完成时间'); return; }
    const riskLevel = autoDetectRisk({ priority: form.priority, type: form.type });
    onCreate({ ...form, riskLevel });
    navigateTo('all');
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button onClick={() => navigateTo('dashboard')} className="mr-3 text-slate-400 hover:text-slate-600"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-bold text-slate-800">创建新任务</h1>
      </div>
      <div className="card p-6 space-y-6">
        <SectionTitle icon={FileText} title="基本信息" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="任务标题 *" value={form.title} onChange={v => handleChange('title', v)} placeholder="请输入任务标题" />
          <FormSelect label="任务类型" value={form.type} onChange={v => handleChange('type', v)} options={TASK_TYPES} />
          <FormInput label="ASIN" value={form.asin} onChange={v => handleChange('asin', v)} placeholder="例如 B08N5WRWNW" />
          <FormInput label="SKU" value={form.sku} onChange={v => handleChange('sku', v)} placeholder="例如 SKU-001" />
          <FormSelect label="店铺" value={form.store} onChange={v => handleChange('store', v)} options={STORES} />
          <FormSelect label="站点" value={form.site} onChange={v => handleChange('site', v)} options={SITES} />
        </div>

        <SectionTitle icon={Users} title="人员分配" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect label="负责人" value={form.assignee} onChange={v => handleChange('assignee', v)} options={USERS} />
          <FormInput label="协作人（用逗号分隔）" value={form.collaborators.join(', ')} onChange={handleCollaborators} placeholder="例如 Alice, Bob" />
          <FormSelect label="优先级" value={form.priority} onChange={v => handleChange('priority', v)} options={PRIORITIES.map(p => p.key)} labels={PRIORITIES.reduce((a, p) => { a[p.key] = p.label; return a; }, {})} />
          <FormInput label="预计完成时间 *" type="date" value={form.dueDate} onChange={v => handleChange('dueDate', v)} />
        </div>

        <SectionTitle icon={Tag} title="标签" />
        <div className="flex items-center space-x-2">
          <input
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="输入标签按回车添加"
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
          />
          <button onClick={addTag} className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">添加</button>
        </div>
        {form.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {form.tags.map(tag => (
              <span key={tag} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                {tag}
                <button onClick={() => removeTag(tag)} className="ml-1 text-indigo-400 hover:text-indigo-700">×</button>
              </span>
            ))}
          </div>
        )}

        <SectionTitle icon={Layers} title="任务内容与材料" />
        <div className="space-y-4">
          <FormTextArea label="输入材料" value={form.materials} onChange={v => handleChange('materials', v)} placeholder="描述完成任务所需的输入材料..." rows={3} />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">上传文件</label>
            <div className="relative border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
              <UploadCloud className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <div className="text-sm text-slate-500">点击或拖拽上传文件（支持批量）</div>
              <input type="file" multiple className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleFileUpload} />
            </div>
            {form.files.length > 0 && (
              <div className="mt-2 space-y-1">
                {form.files.map((f, i) => (
                  <div key={i} className="flex items-center text-sm text-slate-600 bg-slate-50 px-3 py-1.5 rounded">
                    <Paperclip className="w-3.5 h-3.5 mr-2" /> {f.name} <span className="text-slate-400 ml-1">({f.size})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <FormTextArea label="任务描述" value={form.description} onChange={v => handleChange('description', v)} placeholder="详细描述任务目标、要求和注意事项..." rows={4} />
        </div>

        <SectionTitle icon={ShieldCheck} title="审批设置" />
        <div className="flex items-center space-x-3">
          <input id="needLeo" type="checkbox" className="w-4 h-4 text-indigo-600 border-slate-300 rounded" checked={form.needLeoApproval} onChange={e => handleChange('needLeoApproval', e.target.checked)} />
          <label htmlFor="needLeo" className="text-sm text-slate-700">是否需要 Leo 审批（高风险任务将自动开启）</label>
        </div>

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-100">
          <button onClick={() => navigateTo('dashboard')} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">取消</button>
          <button onClick={handleSubmit} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">创建任务</button>
        </div>
      </div>
    </div>
  );
}
